import React, { useEffect, useState } from "react";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import { TOnChangeInput } from "../../data/AppType";
import { prepareMessageFromParams } from "../../utils/AppFunctions";
import ICImage from "../ui/ICImage";
import { cn } from "../../lib";
import { ICModal } from "../ui";
import { crossIcon } from "../../assets/icons/SvgIconList";

const ICFileUpload = ({
  maxFiles,
  maxFileSizeMB,
  acceptedFileTypes,
  onFilesSelected,
  selectedFiles,
  title,
  setSelectedFiles,
  data,
  setData,
  disabled,
  required,
  uniqueMedia = false,
}: {
  title?: string;
  maxFiles: number;
  maxFileSizeMB: number;
  acceptedFileTypes: string[];
  onFilesSelected: (files: File[]) => void;
  selectedFiles: File[];
  setSelectedFiles: (files: File[]) => void;
  data: any;
  setData: any;
  disabled?: boolean;
  required?: boolean;
  uniqueMedia?: boolean;
}) => {
  const { t } = UseTranslationHook();

  const handleFileRemoveNew = (mediaTitle: string) => {
    const updatedData = data.filter((file: any) =>
      file.media_url ? file.media_url !== mediaTitle : file !== mediaTitle
    );
    setData(updatedData);
  };

  const [error, setError] = useState("");
  const [totalSelectedSize, setTotalSelectedSize] = useState(0);

  const getFileCategory = (fileName: string) => {
    const ext = fileName.split(".").pop()?.toLowerCase();
    if (!ext) return "unknown";
    if (["jpg", "jpeg", "png"].includes(ext)) return "image";
    if (["mp4", "m4a"].includes(ext)) return "video";
    if (["pdf"].includes(ext)) return "pdf";
    return "unknown";
  };

  const handleMedia = (files: FileList | null) => {
    if (files && files.length <= maxFiles) {
      const newFiles = Array.from(files).slice(0, maxFiles);

      const uniqueNewFiles = newFiles.filter(
        (newFile) =>
          !selectedFiles?.some(
            (existingFile) => existingFile.name === newFile.name
          )
      );

      let totalSize = totalSelectedSize;
      let exceededSize = false;
      let invalidFileTypes = false;
      let duplicateTypeDetected = false;

      const existingTypes = new Set<string>();
      if (uniqueMedia) {
        [...selectedFiles, ...data].forEach((file: any) => {
          const name = file?.name || file?.media_url || "";
          if (name) {
            const category = getFileCategory(name);
            if (category !== "unknown") existingTypes.add(category);
          }
        });
      }

      for (const newFile of uniqueNewFiles) {
        const fileCategory = getFileCategory(newFile.name);

        if (
          !acceptedFileTypes.some((type) =>
            newFile.name.toLowerCase().endsWith(type)
          )
        ) {
          invalidFileTypes = true;
        } else if (totalSize + newFile.size > maxFileSizeMB * 1024 * 1024) {
          exceededSize = true;
        } else if (uniqueMedia && existingTypes.has(fileCategory)) {
          duplicateTypeDetected = true;
        } else {
          totalSize += newFile.size;
        }
      }

      if (invalidFileTypes) {
        setError(
          prepareMessageFromParams(t("error_message_invalid_file_type"), [
            ["fileType", acceptedFileTypes.join(", ")],
          ])
        );
      } else if (duplicateTypeDetected) {
        setError(
          prepareMessageFromParams(t("error_message_unique_file_type"), [
            [
              "fileType",
              Array.from(existingTypes).join(", ") || t("unknown_type"),
            ],
          ])
        );
      } else if (exceededSize) {
        setError(t("total_file_size_exceeds"));
      } else if (
        selectedFiles.length + uniqueNewFiles.length + data.length >
        maxFiles
      ) {
        setError(
          prepareMessageFromParams(t("error_message_max_file_size"), [
            ["fileSize", maxFiles.toString()],
          ])
        );
      } else {
        setSelectedFiles([...selectedFiles, ...uniqueNewFiles]);
        setTotalSelectedSize(totalSize);
        setError("");
      }
    } else {
      setError(
        prepareMessageFromParams(t("error_message_max_file_size"), [
          ["fileSize", maxFiles.toString()],
        ])
      );
    }
  };

  const handleFileChange = (event: TOnChangeInput) => {
    const files = event.target.files;

    handleMedia(files);
  };

  const [modalOpen, setModalOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const openModal = () => {
    setModalOpen(true);
  };

  const handleFileRemove = (fileName: string) => {
    setSelectedFiles(selectedFiles.filter((file) => file.name !== fileName));
    setError("");
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();

    const files = event.dataTransfer.files;
    handleMedia(files);
    setIsDragging(false);
  };

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const iconMap: { [key: string]: string } = {
    ".pdf": "https://img.icons8.com/color/100/000000/pdf.png",
    ".docx": "https://img.icons8.com/color/48/google-docs--v1.png",
    ".jpeg": "https://img.icons8.com/color/48/000000/jpg.png",
    ".png": "https://img.icons8.com/color/48/000000/png.png",
    ".jpg": "https://img.icons8.com/color/48/000000/jpg.png",
    ".mp4":
      "https://img.icons8.com/?size=100&id=114331&format=png&color=000000",
  };

  const fileName = (url: string) => {
    const fileNameWithExt = url.split(/[/\\]/).pop() || "";
    const fileNameWithoutExt = fileNameWithExt.replace(/\.[^/.]+$/, "");
    const cleanFileName = fileNameWithoutExt.replace(/^\d+-/, "");
    return cleanFileName;
  };
  const getFileName = (path: string) => {
    if (path) {
      const parts = path?.split("/");
      const name = fileName(parts[parts.length - 1]);
      return name.length > 20 ? `${name.substring(0, 17)}...` : name;
    }
  };
  const getFileSizeDisplay = (size: number) => {
    if (size < 1024) {
      return `${size} B`;
    } else if (size < 1048576) {
      return `${(size / 1024).toFixed(2)} KB`;
    } else {
      return `${(size / 1048576).toFixed(2)} MB`;
    }
  };

  useEffect(() => {
    if (required) {
      if (data.length === 0 && selectedFiles.length === 0) {
        setError(t("please_select_file"));
      } else {
        setError("");
      }
    }
  }, [selectedFiles, data]);

  return (
    <>
      <div className="flex">
        <p className="mx-2">{title}</p>
        <div className="info-icon-file-upload">
          <ICImage
            src="https://img.icons8.com/ios/50/000000/info--v1.png"
            containerClass="!w-5"
            onClick={openModal}
          />
        </div>
      </div>
      <div className="w-full border border-gray-400 mt-3">
        {!disabled && (
          <div className="flex h-[150px] p-2">
            <div
              className={cn(`my-drag-drop`, {
                "highlight-drag-part": isDragging,
              })}
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
            >
              {t("drag_and_drop_file")}
            </div>
          </div>
        )}
        {!disabled && (
          <div className="flex justify-center w-full pb-2">
            <input
              type="file"
              accept={acceptedFileTypes.join(",")}
              onChange={handleFileChange}
              className="file-input w-full"
              id={"file-input" + title}
              {...(uniqueMedia || maxFiles === 1 ? {} : { multiple: true })}
            />
            <label className="file-label" htmlFor={"file-input" + title}>
              {maxFiles > 1 ? "Select File(s)" : "Select File"}
            </label>
          </div>
        )}
        {selectedFiles.map((file, index) => (
          <div className="selected-file-div" key={file.name}>
            <div className="selected-upload-file-name-div">
              {iconMap[file.name.slice(file.name.lastIndexOf("."))] ? (
                <ICImage
                  src={iconMap[file.name.slice(file.name.lastIndexOf("."))]}
                  alt={file.name.slice(file.name.lastIndexOf("."))}
                  containerClass="!w-10"
                />
              ) : (
                <ICImage
                  src="https://img.icons8.com/ios/50/000000/file--v1.png"
                  containerClass="!w-10"
                />
              )}
              <div>
                {file.name} ({getFileSizeDisplay(file.size)})
              </div>
            </div>
            <div className="mr-5 w-7 h-7">
              {file.type.startsWith("image/") && (
                <ICImage
                  src={URL.createObjectURL(file)}
                  alt={file.name}
                  className="object-cover w-full h-full"
                />
              )}
            </div>
            {!disabled && (
              <div className="remover">
                <div
                  className="size-5 text-danger cursor-pointer"
                  onClick={() => handleFileRemove(file.name)}
                >
                  {crossIcon}
                </div>
              </div>
            )}
          </div>
        ))}
        {data &&
          data.length > 0 &&
          data?.map((file: any, index: number) => (
            <div className="selected-file-div" key={file.id}>
              <div className="selected-upload-file-name-div">
                <ICImage
                  src={
                    iconMap[
                      file.media_url
                        ? file.media_url.slice(file.media_url.lastIndexOf("."))
                        : file.slice(file.lastIndexOf("."))
                    ]
                  }
                  containerClass="!w-10"
                />
                <div>
                  {file.media_url
                    ? getFileName(file.media_url)
                    : getFileName(file)}
                </div>
              </div>
              <div className="mr-5 w-7 h-7">
                <ICImage
                  imageUrl={file.media_url ? file.media_url : file}
                  className="object-cover w-full h-full"
                />
              </div>
              {!disabled && (
                <div className="remover">
                  <div
                    className="cursor-pointer"
                    onClick={() => {
                      handleFileRemoveNew(
                        file.media_url ? file.media_url : file
                      );
                    }}
                  >
                    ❌
                  </div>
                </div>
              )}
            </div>
          ))}
      </div>
      {error && <div className="error-color-file-upload">{error}</div>}
      {modalOpen && (
        <ICModal
          onClose={() => setModalOpen(false)}
          children={
            <div>{`Maximum upload file is ${maxFileSizeMB} MB.
            You Can upload only ${maxFiles} Files.\n
            ${prepareMessageFromParams(
              t("error_message_invalid_file_type_upload"),
              [["fileType", acceptedFileTypes.join(", ")]]
            )}`}</div>
          }
          title={t("file_info")}
        />
      )}
    </>
  );
};

export default ICFileUpload;
